import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import moment from "moment";
import { format } from "date-fns";
import { enUS, nl } from "date-fns/locale";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { GetBestTravelDates } from "../../services/AppService";
import "./style.scss";
import { useDispatch, useSelector } from "react-redux";
import { getLoginConfig } from "../../redux/slices/loginconfigSlice";
import { getSearchFilters, setTravelDate } from "../../redux/slices/searchFilterSlice";
import useTranslationCustom from "../../i18n/customHook/useTranslation";
import useWindowDimensions from "../../utils/getWindowDimensions";
import { getTravelersData } from "../../helpers/common";

const dateCheckIfLiesUnder = (from, to, check) => {
    const fDate = Date.parse(from);
    const lDate = Date.parse(to);
    const cDate = Date.parse(check);
    return cDate <= lDate && cDate >= fDate;
};

const getPickerLocale = (localeCode) => (localeCode === "nl_NL" ? nl : enUS);

// Keep the library in "pick-start" mode on every click so onChange always
// delivers the just-clicked date as item.selection.startDate.
const FOCUSED_RANGE_START = [0, 0];

const CalenderDateRangePicker = forwardRef(
    ({ show = false, onSelect, onClose, triggerRef, date }, externalRef) => {
        const wrapperRef = useRef();
        const calendarRef = useRef();
        const dispatch = useDispatch();
        const { T } = useTranslationCustom();
        const { width } = useWindowDimensions();
        const loginConfig = useSelector(getLoginConfig);
        const searchfilters = useSelector(getSearchFilters);

        // Use viewport width so tablets in landscape get horizontal layout
        const twoMonthHorizontal = width >= 640;

        const [isReset, setIsReset] = useState(
            !searchfilters.travelDate.startDate && !searchfilters.travelDate.endDate
        );
        const [bestDates, setBestDates] = useState([]);
        const [dateRangeState, setDateRangeState] = useState([
            {
                key: "selection",
                startDate: date?.startDate
                    ? new Date(date.startDate)
                    : searchfilters.travelDate.startDate
                        ? new Date(searchfilters.travelDate.startDate)
                        : new Date(),
                endDate: date?.endDate
                    ? new Date(date.endDate)
                    : searchfilters.travelDate.endDate
                        ? new Date(searchfilters.travelDate.endDate)
                        : new Date(),
            },
        ]);
        const [positionStyle, setPositionStyle] = useState({
            position: "fixed",
            visibility: "hidden",
            top: 0,
            left: 0,
            zIndex: 9999,
        });

        const reposition = useCallback(() => {
            if (!wrapperRef.current) return;
            if (!triggerRef?.current) {
                // Trigger element may not be in the DOM yet (e.g. inner component remount).
                // Retry on next frame — by then all refs are guaranteed to be settled.
                requestAnimationFrame(() => {
                    if (wrapperRef.current && triggerRef?.current) {
                        repositionRef.current();
                    }
                });
                return;
            }

            const trigger = triggerRef.current;
            const wrapper = wrapperRef.current;
            const triggerRect = trigger.getBoundingClientRect();
            const calWidth = wrapper.offsetWidth;
            const calHeight = wrapper.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            let style = { position: "fixed", zIndex: 9999, visibility: "visible" };

            if (vw < 640) {
                // Vertical layout — center in viewport; scroll if taller than viewport
                const maxH = vh - 16;
                const effectiveH = Math.min(calHeight, maxH);
                const top = Math.max(8, Math.round((vh - effectiveH) / 2));
                style.top = top;
                style.left = 8;
                style.right = 8;
                style.width = "auto";
                if (calHeight > maxH) {
                    style.maxHeight = maxH;
                    style.overflowY = "auto";
                }
            } else {
                const triggerMidX = triggerRect.left + triggerRect.width / 2;
                const expandRight = triggerMidX <= vw / 2;

                let left;
                if (expandRight) {
                    left = triggerRect.left;
                } else {
                    left = triggerRect.right - calWidth;
                }
                left = Math.max(8, Math.min(left, vw - calWidth - 8));

                let top = triggerRect.bottom + 8;
                if (top + calHeight > vh - 8) {
                    top = triggerRect.top - calHeight - 8;
                }
                top = Math.max(8, top);

                style.top = top;
                style.left = left;

                // If calendar is taller than the space available (e.g. very small
                // viewport), cap it with scroll so buttons remain reachable.
                const availableH = vh - top - 8;
                if (calHeight > availableH) {
                    style.maxHeight = availableH;
                    style.overflowY = "auto";
                }
            }

            setPositionStyle(style);
        }, [triggerRef]);

        const repositionRef = useRef(reposition);
        repositionRef.current = reposition;

        useLayoutEffect(() => {
            if (show && triggerRef) {
                reposition();
            }
        }, [show, reposition, triggerRef]);

        useEffect(() => {
            if (!show || !triggerRef) return;
            const handler = () => repositionRef.current();
            window.addEventListener("resize", handler);
            window.addEventListener("scroll", handler, true);
            return () => {
                window.removeEventListener("resize", handler);
                window.removeEventListener("scroll", handler, true);
            };
        }, [show, triggerRef]);

        const getBestDatesFromServer = useCallback(
            (date) => {
                const activeDate = date || new Date();
                const month = new Date(activeDate).getMonth();
                const year = new Date(activeDate).getFullYear();
                const { total } = getTravelersData(searchfilters);
                const persona = total === 1 ? "Solo" : total === 2 ? "Couples" : "Family";

                try {
                    Promise.all([
                        GetBestTravelDates({ month: month + 1, persona }),
                        GetBestTravelDates({ month: month + 2, persona }),
                    ]).then((stack) => {
                        let _bestDates = [];
                        stack.forEach((response, index) => {
                            if (response?.bestTravelDates?.length) {
                                const dates = response.bestTravelDates.map((bestDate) => ({
                                    day: bestDate.dayOfMonth,
                                    date: new Date(
                                        moment(`${year}-${month + index + 1}-${bestDate.dayOfMonth}`, "YYYY-M-D").format("YYYY-MM-DD")
                                    ),
                                }));
                                _bestDates = [..._bestDates, ...dates];
                            }
                        });
                        setBestDates(_bestDates);
                    }).catch((error) => { console.error("Error fetching best travel dates", error); });
                } catch (error) { console.error("Error fetching best travel dates", error); }
            }, []);

        useEffect(() => {
            if (show) {
                getBestDatesFromServer(calendarRef?.current?.props?.ranges[0]?.endDate);
            }
        }, [show]);

        useEffect(() => {
            if (dateRangeState?.length) {
                if (
                    dateRangeState[0]?.startDate?.getTime() !==
                    dateRangeState[0]?.endDate?.getTime()
                ) {
                    setIsReset(false);
                }
            }
        }, [dateRangeState]);

        const customDayContent = useCallback(
            (day) => {
                let extraDot = null;
                const getDate = (date) => moment(date).format("YYYY-MM-DD").toString();
                if (bestDates.filter((bD) => getDate(day) === getDate(bD.date)).length > 0) {
                    extraDot = <div className="dateDot" />;
                }
                return (
                    <div>
                        {extraDot}
                        <span>{format(day, "d")}</span>
                    </div>
                );
            },
            [bestDates]
        );

        const resetDateRange = useCallback(() => {
            const focusedDate = dateRangeState[0].startDate;
            const firstDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1);
            const pasteDate =
                focusedDate.getMonth() === new Date().getMonth() ? new Date() : firstDate;

            setDateRangeState([{ startDate: pasteDate, endDate: pasteDate, key: "selection" }]);
            setIsReset(true);
        }, [dateRangeState]);

        const handleRangeChange = useCallback(
            (item) => {
                const cSDate = item?.selection?.startDate;
                const pSSDate = dateRangeState[0]?.startDate;
                const pSEDate = dateRangeState[0]?.endDate;

                const currentDuration = moment.duration(moment(pSEDate).diff(moment(pSSDate)));
                const startDateDuration = moment.duration(moment(cSDate).diff(moment(pSSDate)));
                const endDateDuration = moment.duration(moment(cSDate).diff(moment(pSEDate)));

                const cDays = currentDuration.asDays();
                const sDays = startDateDuration.asDays();
                const eDays = endDateDuration.asDays();

                if (sDays >= 28) {
                    setDateRangeState([{
                        key: "selection",
                        startDate: cSDate,
                        endDate: new Date(moment(cSDate).add(1, "days")),
                    }]);
                } else if (sDays < 28 && sDays > eDays) {
                    if (dateCheckIfLiesUnder(pSSDate, pSEDate, cSDate)) {
                        if (sDays <= 0 || cDays / 2 - sDays >= 0) {
                            setDateRangeState([{ key: "selection", startDate: cSDate, endDate: pSEDate }]);
                        } else if (eDays >= 0 || cDays / 2 + eDays >= 0) {
                            setDateRangeState([{ key: "selection", startDate: pSSDate, endDate: cSDate }]);
                        }
                        return;
                    }
                    setDateRangeState([{ key: "selection", startDate: pSSDate, endDate: cSDate }]);
                }

                if (sDays < 0 && eDays > -28) {
                    setDateRangeState([{ key: "selection", startDate: cSDate, endDate: pSEDate }]);
                } else if (sDays < 0 && eDays < -28) {
                    setDateRangeState([{
                        key: "selection",
                        startDate: cSDate,
                        endDate: new Date(moment(cSDate).add(1, "days")),
                    }]);
                }

                if (
                    dateRangeState[0]?.startDate?.getTime() === dateRangeState[0]?.endDate?.getTime() &&
                    isReset
                ) {
                    setDateRangeState([{
                        key: "selection",
                        startDate: cSDate,
                        endDate: new Date(moment(cSDate).add(1, "days")),
                    }]);
                }
            },
            [isReset, dateRangeState]
        );

        const handleSelect = useCallback(() => {
            dispatch(
                setTravelDate({
                    startDate: isReset ? "" : new Date(dateRangeState[0].startDate).toDateString(),
                    endDate: isReset ? "" : new Date(dateRangeState[0].endDate).toDateString(),
                })
            );
            onSelect?.();
        }, [dateRangeState, isReset, onSelect]);

        if (!show) return null;

        const calendarContent = (
            <>
                {triggerRef && (
                    <div
                        className="calendar-backdrop"
                        onClick={onClose ?? onSelect}
                    />
                )}
                <div
                    ref={(el) => {
                        wrapperRef.current = el;
                        if (typeof externalRef === "function") externalRef(el);
                        else if (externalRef) externalRef.current = el;
                    }}
                    className="calendar-wrapper checkin-cal"
                    style={triggerRef ? positionStyle : undefined}
                >
                    {/* Clip only the months grid — buttons live outside this wrapper */}
                    <div className="calendar-months-clip">
                        <DateRangePicker
                            ref={calendarRef}
                            onChange={handleRangeChange}
                            minDate={new Date()}
                            maxDate={new Date(moment().add(332, "days"))}
                            dayContentRenderer={customDayContent}
                            months={2}
                            ranges={dateRangeState}
                            dragSelectionEnabled={false}
                            showMonthAndYearPickers={false}
                            showPreview={false}
                            showDateDisplay={false}
                            weekStartsOn={1}
                            direction={twoMonthHorizontal ? "horizontal" : "vertical"}
                            locale={getPickerLocale(loginConfig?.locale?.code)}
                            focusedRange={FOCUSED_RANGE_START}
                            onRangeFocusChange={() => { }}
                            onShownDateChange={(date) => getBestDatesFromServer(date)}
                        />
                    </div>
                    <div className="calendarBtns">
                        <a className="reset-btn" onClick={resetDateRange}>
                            {T("Buttons:Reset")}
                        </a>
                        <a className="reset-btn select" onClick={handleSelect}>
                            {T("Buttons:Select")}
                        </a>
                    </div>
                </div>
            </>
        );

        return triggerRef
            ? ReactDOM.createPortal(calendarContent, document.body)
            : calendarContent;
    }
);

CalenderDateRangePicker.displayName = "CalenderDateRangePicker";

CalenderDateRangePicker.propTypes = {
    show: PropTypes.bool,
    onSelect: PropTypes.func,
    onClose: PropTypes.func,
    triggerRef: PropTypes.object,
    date: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }),
};

export default CalenderDateRangePicker;
